import { useState } from "react";
import { useSelector } from "react-redux";
import Button from "../../components/Elements/Button";
import { success, error } from "../../components/notify";
import DrawerHeader from "../Header/DrawerHeader";
import Layout from "../../layout/Layout";
import OtpInput from "../Elements/OtpInput";
import { Edit } from "../Icon/Edit";
import { useOtpTimer } from "../../hooks/useOtpTimer";
import { useEmailOrPhoneNumberValidationWithCaptcha } from "../../hooks/useEmailOrPhoneNumberValidationWithCaptcha";
import { ModalConstants } from "../../constants/modals/ModalConstants";
import { FLOW_TYPES } from "@/constants/modals/FlowTypes";
import CaptchaBox from "../capchaBox/CapchaBox";

const FLOW_CONFIG = {
  [FLOW_TYPES.UNSUBSCRIBE]: {
    title: "Unsubscribe",
    stepOneDescription: "Enter your email or phone to unsubscribe.",
    stepTwoDescription: "Enter the OTP sent to your email/phone to confirm unsubscription.",
    primaryButtonLabel: "Unsubscribe",
    verifyButtonLabel: "Verify & Unsubscribe",
    otpSentMessage: "We have sent an OTP to your contact.",
    verifySuccessMessage: "You have been unsubscribed successfully.",
  },
  [FLOW_TYPES.SUBSCRIBE]: {
    title: "Subscribe",
    stepOneDescription: "Enter your email or phone to subscribe.",
    stepTwoDescription: "Enter the OTP sent to your email/phone to confirm subscription.",
    primaryButtonLabel: "Subscribe",
    verifyButtonLabel: "Verify & Subscribe",
    otpSentMessage: "We have sent an OTP for subscription.",
    verifySuccessMessage: "You have been subscribed successfully.",
  },
  [FLOW_TYPES.OPT_OUT]: {
    title: "Merchant Opt Out",
    stepOneDescription: "Enter your email or phone to opt out.",
    stepTwoDescription: "Enter the OTP sent to confirm opt out.",
    primaryButtonLabel: "Opt Out",
    verifyButtonLabel: "Verify & Opt Out",
    otpSentMessage: "OTP sent to confirm your opt out.",
    verifySuccessMessage: "You have successfully opted out.",
  },
  [FLOW_TYPES.OPT_IN]: {
    title: "Opt In",
    stepOneDescription: "Enter your email or phone to opt in.",
    stepTwoDescription: "Enter the OTP sent to confirm opt in.",
    primaryButtonLabel: "Opt In",
    verifyButtonLabel: "Verify & Opt In",
    otpSentMessage: "OTP sent to confirm your opt in.",
    verifySuccessMessage: "You have successfully opted in.",
  },
};

const EmailPhoneOtpModal = ({
  className,
  onClose,
  fromWhere = "",
  flowType = FLOW_TYPES.UNSUBSCRIBE,
  // API functions (you can reuse component by just swapping these)
  requestOtp = () => {}, // (emailOrPhone, flowType) => Promise
  verifyOtp = () => {},  // ({ emailOrPhone, otp, flowType }) => Promise
}) => {
  const websiteColor = useSelector((state) => state?.onLoadData?.color);

  const config = FLOW_CONFIG[flowType] || FLOW_CONFIG[FLOW_TYPES.UNSUBSCRIBE];

  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(
    ModalConstants.UNSUBSCRIBE_MODAL_STEPS.EMAIL_PHONE
  );
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { validationError, validateInput } =
    useEmailOrPhoneNumberValidationWithCaptcha(emailOrPhone, captchaToken);
  const { timeLeft, resetTimer, startTimer } = useOtpTimer(
    ModalConstants.OTP.TIMER_SECONDS
  );

  const handleEmailOrPhoneChange = (e) => setEmailOrPhone(e.target.value);
  const handleOtpChange = (otpValue) => setOtp(otpValue);
  const handleCaptchaChange = (token) => setCaptchaToken(token);

  const goBackToEmailStep = () => {
    setStep(ModalConstants.UNSUBSCRIBE_MODAL_STEPS.EMAIL_PHONE);
    resetTimer();
  };

  const handleAction = async () => {
    // STEP 1: request OTP
    if (step === ModalConstants.UNSUBSCRIBE_MODAL_STEPS.EMAIL_PHONE) {
      const isValid = validateInput();
      if (!isValid) return;

      setIsLoading(true);
      try {
        // Let the parent decide status handling / throwing errors
        await requestOtp(emailOrPhone, flowType);

        success(config.otpSentMessage);
        setStep(ModalConstants.UNSUBSCRIBE_MODAL_STEPS.OTP);
        startTimer();
      } catch (e) {
        error(ModalConstants.MESSAGES.REQUEST_FAILED || "Failed to process request.");
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // STEP 2: verify OTP
    if (otp.length !== ModalConstants.OTP.LENGTH) {
      error(ModalConstants.MESSAGES.INVALID_OTP || "Invalid OTP.");
      return;
    }

    setIsLoading(true);
    try {
      await verifyOtp({ emailOrPhone, otp, flowType });
      success(config.verifySuccessMessage);
      onClose?.();
    } catch (e) {
      error(
        ModalConstants.MESSAGES.OTP_VERIFICATION_FAILED ||
          "Failed to verify OTP."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const StepOne = (
    <>
      <label className="block mb-2 text-sm">
        {ModalConstants.LABELS.EMAIL_PHONE}{" "}
        <span className="text-primary">
          {ModalConstants.LABELS.REQUIRED_MARK}
        </span>
      </label>
      <input
        value={emailOrPhone}
        onChange={handleEmailOrPhoneChange}
        type="text"
        className="bg-white-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:border-blue-500 focus:outline-none block w-full ps-3.5 p-2.5"
        placeholder={ModalConstants.PLACEHOLDERS.EMAIL_PHONE}
        autoComplete="off"
      />
      {validationError && (
        <span className="text-primary text-sm">{validationError}</span>
      )}

      <CaptchaBox visible={true} onToken={handleCaptchaChange} />

      <Button
        onClick={handleAction}
        disabled={isLoading}
        style={{ backgroundColor: websiteColor }}
        className="text-white w-full mt-5 rounded-lg text-sm inline-flex justify-center items-center px-5 py-2.5"
      >
        {isLoading
          ? ModalConstants.BUTTON_TEXT.PROCESSING
          : config.primaryButtonLabel}
      </Button>
    </>
  );

  const StepTwo = (
    <>
      <div className="mb-4">
        <span className="text-sm">
          {ModalConstants.LABELS.OTP_SENT_TO}{" "}
          <span className="font-medium">{emailOrPhone}</span>
          <Button onClick={goBackToEmailStep} className="text-xs px-2 py-1">
            <Edit />
          </Button>
        </span>
      </div>

      <OtpInput
        length={ModalConstants.OTP.LENGTH}
        onOtpSubmit={handleOtpChange}
        isDisabled={isLoading}
      />

      <div className="text-center mt-4 md:mt-6">
        <span className="text-p6 font-bold">{timeLeft}</span>
      </div>

      <Button
        onClick={handleAction}
        disabled={isLoading || otp.length !== ModalConstants.OTP.LENGTH}
        style={{ backgroundColor: websiteColor }}
        className="text-white w-full mt-5 rounded-lg text-sm inline-flex justify-center items-center px-5 py-2.5"
      >
        {isLoading
          ? ModalConstants.BUTTON_TEXT.VERIFYING
          : config.verifyButtonLabel}
      </Button>
    </>
  );

  const Body =
    step === ModalConstants.UNSUBSCRIBE_MODAL_STEPS.EMAIL_PHONE
      ? StepOne
      : StepTwo;

  const commonLayout = (
    <div className="w-full md:w-[448px]">
      <DrawerHeader
        title={config.title}
        onClose={onClose}
        description={
          step === ModalConstants.UNSUBSCRIBE_MODAL_STEPS.EMAIL_PHONE
            ? config.stepOneDescription
            : config.stepTwoDescription
        }
      />
      <div className="mt-4 px-6">{Body}</div>
    </div>
  );

  return fromWhere === ModalConstants.FROM_WHERE.FOOTER ? (
    <div className={`w-full md:w-[448px] ${className} pb-12 mdl:pb-8`}>
      {commonLayout}
    </div>
  ) : (
    <Layout className="bg-slate-100 min-h-screen w-full">
      <div className="min-h-screen bg-white 2xl:px-4 2xl:mx-auto 2xl:w-[1170px]">
        <div className="px-4 mx-auto lg:w-3/5 w-full">
          <div className="m-0 p-4 bg-slate-100 2xl:h-[100px]">
            <div className="2xl:text-3xl font-bold text-xl">
              {config.title}
            </div>
            <div className="2xl:text-sm font-bold text-xs">
              {step === ModalConstants.UNSUBSCRIBE_MODAL_STEPS.EMAIL_PHONE
                ? config.stepOneDescription
                : config.stepTwoDescription}
            </div>
          </div>
          <div className="mt-4">{commonLayout}</div>
        </div>
      </div>
    </Layout>
  );
};

export default EmailPhoneOtpModal;
